// /talk: the one conversion surface. The form is not a contact form; it is a
// work item being opened on the firm's board, in Maestro's own vocabulary.
// Interests double as lanes and as lead attribution.

export const INTERESTS = [
  { id: 'maestro', accent: 19 },
  { id: 'strategy', accent: 19 },
  { id: 'workshops', accent: 10 },
  { id: 'sdlc', accent: 1 },
  { id: 'discovery', accent: 10 },
  { id: 'fde', accent: 1 },
  { id: 'advisory', accent: 19 },
  { id: 'partners', accent: 10 },
  { id: 'speaking', accent: 19 },
]

const talkContent = {
  'en-US': {
    seo: {
      title: 'Talk to Us | Lean it 101',
      description:
        'Open a card on our board: tell us what hurts, pick the lane, and get an answer from the people who will actually do the work.',
    },
    hero: {
      kicker: 'Talk to us',
      headline: 'Open a card on our board.',
      body: 'This is how work reaches us: as a card, in a lane, answered by the people who will do it. Yours starts here.',
    },
    booking: {
      lead: 'Prefer a call on the calendar?',
      cta: 'Book a conversation',
    },
    form: {
      laneLabel: 'Pick the lane',
      titleLabel: 'What hurts, in one line',
      titlePlaceholder: 'Support backlog doubles every quarter',
      detailLabel: 'Context, if you want to add it',
      detailPlaceholder: 'Systems involved, what you tried, what a good outcome looks like.',
      nameLabel: 'Your name',
      emailLabel: 'Work email',
      companyLabel: 'Company',
      submit: 'Put it on the board',
      submitting: 'Adding to the board...',
      privacy: 'Goes straight to our CRM and to a human. No sequences, no spam.',
      errorRequired: 'Name, email and the one-line title are the minimum for a card.',
      errorSend: 'That did not go through. Try again, or write to contact@leanit101.com.',
    },
    interests: {
      maestro: 'Maestro on my flow',
      strategy: 'Strategic planning',
      workshops: 'Workshop facilitation',
      sdlc: 'Software delivery and AI',
      discovery: 'Discovery and assessment',
      fde: 'FDE pod',
      advisory: 'Executive advisory',
      partners: 'Partnership',
      speaking: 'Speaking invitation',
    },
    card: {
      preview: 'Your card, as our board will see it',
      untitled: 'Your one-line title appears here',
      waits: 'waits on Lean it 101',
      submittedBadge: 'ON THE BOARD',
      submittedTitle: 'Card opened.',
      submittedBody: 'A human reads it today and answers within one business day. If it is urgent, say so in the title next time; urgent cards jump the queue.',
      responseChip: 'Answer · within 1 business day',
    },
    faq: {
      kicker: 'Before you ask',
      items: [
        {
          q: 'How does an engagement usually start?',
          a: 'With one conversation about where it hurts. From there we propose a shape: usually a short discovery or a workshop, with duration and deliverables named before any commitment.',
        },
        {
          q: 'Remote or on site?',
          a: 'Both. Workshops and strategy sessions are strongest in the room; engineering pods work remotely with on-site moments where they pay off. Brazil and the US are both home ground.',
        },
        {
          q: 'How do you price?',
          a: 'Per engagement, in a written proposal, after the first conversation. Every service on the catalogue has a defined shape, so the proposal is specific rather than an hourly guess.',
        },
        {
          q: 'Who actually does the work?',
          a: 'The people you talk to. Senior practitioners direct the pods and the agents; nothing is sold by one team and delivered by another.',
        },
        {
          q: 'We are a reseller or integrator. Same door?',
          a: 'Same door, different lane: pick Partnership above and tell us about your clients. Platform partnerships come with delivery support behind them.',
        },
      ],
    },
  },

  'pt-BR': {
    seo: {
      title: 'Fale com a Gente | Lean it 101',
      description:
        'Abra um cartão no nosso board: diga onde dói, escolha a raia, e receba resposta de quem vai fazer o trabalho.',
    },
    hero: {
      kicker: 'Fale com a gente',
      headline: 'Abra um cartão no nosso board.',
      body: 'É assim que o trabalho chega até nós: como um cartão, numa raia, respondido por quem vai executar. O seu começa aqui.',
    },
    booking: {
      lead: 'Prefere uma conversa na agenda?',
      cta: 'Agendar uma conversa',
    },
    form: {
      laneLabel: 'Escolha a raia',
      titleLabel: 'Onde dói, em uma linha',
      titlePlaceholder: 'O backlog de suporte dobra a cada trimestre',
      detailLabel: 'Contexto, se quiser acrescentar',
      detailPlaceholder: 'Sistemas envolvidos, o que já tentaram, como seria um bom resultado.',
      nameLabel: 'Seu nome',
      emailLabel: 'Email corporativo',
      companyLabel: 'Empresa',
      submit: 'Colocar no board',
      submitting: 'Adicionando ao board...',
      privacy: 'Vai direto para o nosso CRM e para um humano. Sem sequências, sem spam.',
      errorRequired: 'Nome, email e o título de uma linha são o mínimo de um cartão.',
      errorSend: 'Não foi desta vez. Tente de novo, ou escreva para contact@leanit101.com.',
    },
    interests: {
      maestro: 'Maestro no meu fluxo',
      strategy: 'Planejamento estratégico',
      workshops: 'Facilitação de workshops',
      sdlc: 'Entrega de software e IA',
      discovery: 'Discovery e assessment',
      fde: 'Pod FDE',
      advisory: 'Advisory executivo',
      partners: 'Parceria',
      speaking: 'Convite para palestra',
    },
    card: {
      preview: 'Seu cartão, como o nosso board vai ver',
      untitled: 'Seu título de uma linha aparece aqui',
      waits: 'esperando a Lean it 101',
      submittedBadge: 'NO BOARD',
      submittedTitle: 'Cartão aberto.',
      submittedBody: 'Um humano lê hoje e responde em até um dia útil. Se for urgente, diga no título da próxima vez; cartões urgentes furam a fila.',
      responseChip: 'Resposta · em até 1 dia útil',
    },
    faq: {
      kicker: 'Antes de perguntar',
      items: [
        {
          q: 'Como um engajamento costuma começar?',
          a: 'Com uma conversa sobre onde dói. Dali propomos uma forma: normalmente um discovery curto ou um workshop, com duração e entregas nomeadas antes de qualquer compromisso.',
        },
        {
          q: 'Remoto ou presencial?',
          a: 'Os dois. Workshops e sessões de estratégia rendem mais na sala; pods de engenharia trabalham remoto com momentos presenciais onde valem a pena. Brasil e Estados Unidos são casa.',
        },
        {
          q: 'Como vocês precificam?',
          a: 'Por engajamento, em proposta escrita, depois da primeira conversa. Cada serviço do catálogo tem forma definida, então a proposta é específica em vez de um chute por hora.',
        },
        {
          q: 'Quem faz o trabalho de verdade?',
          a: 'As pessoas com quem você fala. Praticantes seniores dirigem os pods e os agentes; nada é vendido por um time e entregue por outro.',
        },
        {
          q: 'Somos revenda ou integradora. Mesma porta?',
          a: 'Mesma porta, outra raia: escolha Parceria acima e conte dos seus clientes. Parcerias de plataforma vêm com suporte de entrega por trás.',
        },
      ],
    },
  },

  es: {
    seo: {
      title: 'Habla con Nosotros | Lean it 101',
      description:
        'Abre una tarjeta en nuestro tablero: cuenta dónde duele, elige el carril, y recibe respuesta de quienes harán el trabajo.',
    },
    hero: {
      kicker: 'Habla con nosotros',
      headline: 'Abre una tarjeta en nuestro tablero.',
      body: 'Así llega el trabajo hasta nosotros: como una tarjeta, en un carril, respondida por quienes van a ejecutar. La tuya empieza aquí.',
    },
    booking: {
      lead: '¿Prefieres una llamada en la agenda?',
      cta: 'Agendar una conversación',
    },
    form: {
      laneLabel: 'Elige el carril',
      titleLabel: 'Dónde duele, en una línea',
      titlePlaceholder: 'El backlog de soporte se duplica cada trimestre',
      detailLabel: 'Contexto, si quieres agregarlo',
      detailPlaceholder: 'Sistemas involucrados, qué intentaron, cómo sería un buen resultado.',
      nameLabel: 'Tu nombre',
      emailLabel: 'Email corporativo',
      companyLabel: 'Empresa',
      submit: 'Ponerla en el tablero',
      submitting: 'Agregando al tablero...',
      privacy: 'Va directo a nuestro CRM y a un humano. Sin secuencias, sin spam.',
      errorRequired: 'Nombre, email y el título de una línea son el mínimo de una tarjeta.',
      errorSend: 'No se pudo enviar. Inténtalo de nuevo, o escribe a contact@leanit101.com.',
    },
    interests: {
      maestro: 'Maestro en mi flujo',
      strategy: 'Planificación estratégica',
      workshops: 'Facilitación de workshops',
      sdlc: 'Entrega de software e IA',
      discovery: 'Discovery y assessment',
      fde: 'Pod FDE',
      advisory: 'Advisory ejecutivo',
      partners: 'Alianza',
      speaking: 'Invitación a charla',
    },
    card: {
      preview: 'Tu tarjeta, como la verá nuestro tablero',
      untitled: 'Tu título de una línea aparece aquí',
      waits: 'espera a Lean it 101',
      submittedBadge: 'EN EL TABLERO',
      submittedTitle: 'Tarjeta abierta.',
      submittedBody: 'Un humano la lee hoy y responde dentro de un día hábil. Si es urgente, dilo en el título la próxima vez; las tarjetas urgentes saltan la fila.',
      responseChip: 'Respuesta · dentro de 1 día hábil',
    },
    faq: {
      kicker: 'Antes de preguntar',
      items: [
        {
          q: '¿Cómo suele empezar un compromiso?',
          a: 'Con una conversación sobre dónde duele. Desde ahí proponemos una forma: normalmente un discovery corto o un workshop, con duración y entregas nombradas antes de cualquier compromiso.',
        },
        {
          q: '¿Remoto o presencial?',
          a: 'Ambos. Los workshops y las sesiones de estrategia rinden más en la sala; los pods de ingeniería trabajan en remoto con momentos presenciales donde valen la pena. Brasil y Estados Unidos son casa.',
        },
        {
          q: '¿Cómo cobran?',
          a: 'Por compromiso, en propuesta escrita, después de la primera conversación. Cada servicio del catálogo tiene forma definida, así que la propuesta es específica en lugar de una estimación por hora.',
        },
        {
          q: '¿Quién hace el trabajo realmente?',
          a: 'Las personas con las que hablas. Practicantes seniores dirigen los pods y los agentes; nada se vende por un equipo y se entrega por otro.',
        },
        {
          q: 'Somos reseller o integrador. ¿La misma puerta?',
          a: 'La misma puerta, otro carril: elige Alianza arriba y cuéntanos de tus clientes. Las alianzas de plataforma vienen con soporte de entrega detrás.',
        },
      ],
    },
  },
}

const normalize = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

export const getTalkContent = (language) => talkContent[normalize(language)]
export default talkContent
